import { useEffect, useRef, useState } from 'react';
import { ErreurApi } from '../api/client';
import {
  useActualiserFacture, useCommande, useReleverFortemix, useReleverSuivi,
} from '../api/hooks';
import {
  LIBELLE_CANAL,
  type ActionCalculee, type CanalEnvoi, type CommandeDetaillee, type DocumentReglages,
} from '../api/types';
import {
  cibleRelance, codePostalDepuisAdresse, contientProduitFortelock,
  estCommandeFortemix, estFactureSellsy, expedieLuiMeme,
  REGLAGES_PAR_DEFAUT, transporteurDepuisTracking, type CibleRelance,
} from '@mosaik/core';
import {
  useActionCommande, type ModalesCommande,
} from './actions-commande';
import { ConfirmerLivraison } from './ConfirmerLivraison';
import { CommandeIntrouvable } from './CommandeIntrouvable';
import { LienBoutique, LienExpedition } from './Liens';
import { dateHeure, euros, formatCourt, libelleFournisseurs } from '../format';
import { Chargement, Erreur } from './Etats';
import { EtapesCommande, FenetreAvis, FenetreReglement } from './EtapesCommande';
import { ChangementsShopify } from './ChangementsShopify';
import { ColisSupplementaires } from './ColisSupplementaires';
import { EncartLitiges } from './Litiges';
import { AdresseCommande } from './AdresseCommande';
import { NoteCommande } from './NoteCommande';
import { useToasts } from './Toasts';

/** Ordre d'affichage des envois en échec : du plus amont au plus aval. */
const ENVOIS: CanalEnvoi[] = ['fournisseur', 'expedition', 'tracking'];

export function FicheCommande({
  id, reglages, onFermer, modales,
}: {
  id: string;
  reglages: DocumentReglages | undefined;
  onFermer: () => void;
  modales: ModalesCommande;
}) {
  const { data: commande, isLoading, error, refetch } = useCommande(id);
  const panneau = useRef<HTMLElement>(null);

  // Échap ferme, et le focus part dans le panneau à l'ouverture : la fiche
  // s'utilise entièrement au clavier.
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    panneau.current?.focus();
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  return (
    <>
      <div className="scrim" onClick={onFermer} aria-hidden="true" />
      <aside
        className="drawer" role="dialog" aria-modal="true" aria-label="Détail de la commande"
        ref={panneau} tabIndex={-1}
      >
        {isLoading && <div className="dbody"><Chargement /></div>}
        {error && <div className="dbody"><Erreur erreur={error} onReessayer={() => void refetch()} /></div>}
        {commande && (
          <Contenu
            commande={commande} reglages={reglages} onFermer={onFermer} modales={modales}
          />
        )}
      </aside>
    </>
  );
}

function Contenu({
  commande, reglages, onFermer, modales,
}: {
  commande: CommandeDetaillee;
  reglages: DocumentReglages | undefined;
  onFermer: () => void;
  modales: ModalesCommande;
}) {
  const { ajouter } = useToasts();
  const action = commande.calcul.actions[0];

  const [erreurReleve, setErreurReleve] = useState<string | null>(null);
  /** Ce que le dernier clic sur « Relever maintenant » a donné, en clair. */
  const [verdictReleve, setVerdictReleve] = useState<string | null>(null);
  /** L'encart Articles tient sur une ligne tant qu'on ne le déplie pas. */
  const [articlesOuverts, setArticlesOuverts] = useState(false);

  /**
   * Transporteur que l'API va réellement interroger.
   *
   * Même règle que côté serveur : un format CERTAIN l'emporte sur le
   * transporteur saisi — un numéro en `FRT-` est un envoi DHL même si la
   * commande dit « Autre ». Trois commandes sont exactement dans ce cas, et
   * afficher le transporteur saisi ferait croire à un relevé qui n'a pas lieu.
   */
  const detectionEnregistree = transporteurDepuisTracking(commande.tracking);
  const transporteurInterroge = (detectionEnregistree?.confiance === 'certaine'
    ? detectionEnregistree.transporteur
    : commande.transporteur ?? detectionEnregistree?.transporteur) ?? 'le transporteur';

  /**
   * Code postal dont disposera le relevé, champ dédié ou adresse.
   *
   * DPD en fait un second facteur, et les commandes Sellsy le portent dans le
   * bloc `detailsLivraison` plutôt que dans le champ dédié. Quand il manque
   * partout, on ouvre un champ plutôt que de renvoyer l'utilisateur corriger la
   * commande — il pourra vérifier le transporteur tout de suite.
   */
  const codePostalConnu = codePostalDepuisAdresse(commande.adresse);
  // Commande close, mais un colis supplémentaire encore en route : son relevé
  // reste ouvert, lui seul.
  const colisEnRoute = commande.colis.some((c) => !c.livre);
  const suiviClos = commande.livre && !colisEnRoute;
  const [codePostalSaisi, setCodePostalSaisi] = useState('');

  const releve = useReleverSuivi();
  const actualiserFacture = useActualiserFacture();
  /** Relit la facture Sellsy ; ce qui a changé part dans un toast et à l'historique. */
  async function actualiserDepuisSellsy() {
    try {
      const r = await actualiserFacture.mutateAsync(commande.id);
      ajouter(r.message, 'ok');
      for (const a of r.avertissements) ajouter(a, 'bad');
    } catch (e) {
      ajouter(e instanceof ErreurApi ? e.message : 'La facture n’a pas pu être relue chez Sellsy.', 'bad');
    }
  }
  // Le même exécuteur que la liste : cliquer l'action ici ou là-bas fait
  // exactement la même chose, par le même code.
  const { executer, confirmerLivree, remettreEnCours, enAttente: actionEnAttente } =
    // Les erreurs d'action s'affichent en toast : il n'y a plus de formulaire
    // dans la fiche pour les porter.
    useActionCommande(reglages, modales);
  const [confirmationLivraison, setConfirmationLivraison] = useState(false);
  /** La fenêtre ouverte depuis la liste des étapes. */
  /**
   * Les fenêtres propres à la fiche. Les mails, la saisie et la relecture d'un
   * mail envoyé vivent dans `App` : la liste les ouvre aussi, et c'est la même
   * fenêtre des deux côtés.
   */
  const [fenetreEtape, setFenetreEtape] = useState<null | 'reglement' | 'avis'>(null);

  const enAttente = actionEnAttente;
  /** Qui relancer, quand l'action due est une relance. */
  const cibleRelanceDue = action ? relanceDe(action, commande, reglages) : null;

  return (
    <>
      <div className="dhead">
        <div>
          <h1 className="mono" style={{ fontSize: 18 }}>
            {/* Même lien que dans la liste : la référence ouvre la commande là
                où elle est née. Sans identifiant, elle reste du texte. */}
            {commande.liens.boutique
              ? <LienBoutique commande={commande}>{commande.ref}</LienBoutique>
              : commande.ref}
          </h1>
          <p>
            {commande.client}
            {commande.ville ? ` · ${commande.ville}` : ''}
            {commande.pays && commande.pays !== 'France' ? ` (${commande.pays})` : ''}
          </p>
        </div>
        <button className="x" onClick={onFermer} aria-label="Fermer la fiche">&times;</button>
      </div>

      <div className="dbody">
        {/*
          * Les étapes, cochées d'après l'état de la commande.
          *
          * Chaque libellé ouvre le geste de son étape : envoyer le mail s'il
          * reste à faire, relire celui qui a été envoyé sinon. Un mail client
          * déjà parti se relit d'abord — le refaire est un choix, offert dans
          * la fenêtre, pour le client qui dit n'avoir rien reçu.
          */}
        <div className="fieldset">
          <EtapesCommande
            commande={commande}
            gestes={{
              onReglement: () => setFenetreEtape('reglement'),
              onEnlevement: () => modales.onEnlevement(commande),
              onFournisseur: () => {
                if (commande.calcul.etape !== 'recue') { modales.onVoirMail(commande, 'fournisseur'); return; }
                // L'action du moteur quand elle existe : elle porte le signal
                // de facture non réglée, et donc sa confirmation. Absente —
                // la commande attend son règlement —, le geste reste possible,
                // mais la confirmation doit le rester aussi.
                const impayee = !commande.etapes.reglement.fait && commande.paiement?.regle === false;
                void executer(commande, commande.calcul.actions.find((a) => a.cle === 'fournisseur') ?? {
                  cle: 'fournisseur', titre: 'Envoyer au fournisseur', gravite: 1, badge: '', detail: '',
                  blocage: impayee
                    ? 'Facture non réglée : la commande attend son règlement. La passer quand même ?'
                    : null,
                });
              },
              // La saisie de la donnée ET son mail au client, dans une seule
              // fenêtre : voir le mail parti, ou saisir la valeur et l'envoyer.
              onExpedition: () => modales.onSaisie(commande, 'expedition'),
              onTracking: () => modales.onSaisie(commande, 'tracking'),
              onLivre: commande.livre ? undefined : () => setConfirmationLivraison(true),
              onAvis: () => setFenetreEtape('avis'),
              // Clôturer garde sa confirmation ; rouvrir est le geste qu'on
              // trouvait en bas de fiche, sans détour.
              onBasculerLivre: () => {
                if (commande.livre) void remettreEnCours(commande);
                else setConfirmationLivraison(true);
              },
            }}
          />

          {/* Réservé aux commandes Fortelock (`FCK`, `UTM`) : les seules dont
              le portail Fortemix permet de vérifier la présence chez le
              fournisseur, et donc de lever le constat ensuite. */}
          {cibleRelanceDue && action?.cle === 'relance'
            && contientProduitFortelock(commande.articles.map((a) => a.reference)) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
              <CommandeIntrouvable commande={commande} enAttente={enAttente} />
            </div>
          )}
        </div>

        {/* Le récapitulatif d'abord, juste sous les cases : ce qu'est la
            commande avant ce qui lui arrive. */}
        <div className="fieldset">
          <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            Récapitulatif
            {estFactureSellsy(commande.ref) && (
              <button
                type="button" className="btn sm" disabled={actualiserFacture.isPending}
                title="Relit la facture chez Sellsy : société, montant, frais de port, adresse de livraison, règlement."
                onClick={() => void actualiserDepuisSellsy()}
              >
                {actualiserFacture.isPending ? <><span className="spin" /> …</> : 'Actualiser depuis Sellsy'}
              </button>
            )}
          </h3>
          <dl className="kv">
            <dt>Montant</dt><dd>{euros(commande.montant)}</dd>
            <dt>Commandée le</dt><dd>{formatCourt(commande.dateReception)}</dd>
            <dt>Fournisseur</dt>
            <dd>
              {commande.fournisseurs.length
                ? libelleFournisseurs(commande.fournisseurs)
                : <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>indéterminé</span>}
            </dd>
            <dt>Transmise le</dt><dd>{formatCourt(commande.dateEnvoiFournisseur)}</dd>
            {commande.montantLivraison !== null && (
              <><dt>Dont livraison</dt><dd>{euros(commande.montantLivraison)}</dd></>
            )}
            {/* La note arrivée avec la commande — consigne du client, « Réappro TPZ ».
                Distincte de la note de l'équipe, juste en dessous. */}
            {commande.note && <><dt>Note de commande</dt><dd>{commande.note}</dd></>}
          </dl>
          <AdresseCommande commande={commande} />
          <NoteCommande commande={commande} />
        </div>

        <ChangementsShopify commande={commande} />

        <EncartLitiges litiges={commande.calcul.litiges} />

        {ENVOIS.filter((c) => commande.envois[c].etat === 'echec').map((canal) => (
          <div
            key={canal} className="fieldset"
            style={{ borderLeft: '4px solid var(--rouge)', background: 'var(--brick-soft)' }}
          >
            <h3 style={{ color: 'var(--brick)' }}>{LIBELLE_CANAL[canal]} — non parti</h3>
            <p style={{ fontSize: 12.5, color: 'var(--brick)', marginBottom: 10 }}>
              {commande.envois[canal].derniereErreur ?? 'Cause inconnue.'}{' '}
              {canal === 'fournisseur'
                ? "Le fournisseur n'a rien reçu."
                : "Le client n'a pas été prévenu."}
              {commande.envois[canal].tentatives > 1 && ` ${commande.envois[canal].tentatives} tentatives.`}
            </p>
            {/* Un mail ne se « rejoue » pas à l'identique : il se relit.
                S'il a échoué, c'est qu'il manquait quelque chose, et le
                renvoyer tel quel échouerait pareil. Les trois canaux rouvrent
                donc leur fenêtre de relecture. */}
            <button
              type="button" className="btn pri sm" disabled={enAttente}
              onClick={() => {
                if (canal !== 'fournisseur') { modales.onMailClient(commande, canal); return; }
                // Repasser quand le fournisseur ne retrouvait pas la commande,
                // transmettre sinon : c'est la même distinction que dans le
                // moteur, et elle se lit sur la commande.
                void executer(commande, {
                  ...action!,
                  cle: commande.incident === 'COMMANDE_MANQUANTE' ? 'probleme' : 'fournisseur',
                });
              }}
            >
              Reprendre le mail
            </button>
          </div>
        ))}

        {/* Le portail fournisseur remplit la date et le tracking des cases :
            sa place est ici, pas dans le suivi transporteur — qui interroge le
            TRANSPORTEUR, une fois le colis parti. */}
        {/* Le règlement se lit sur sa case, en tête de fiche : l'encadré qui
            le répétait ici a rejoint la fenêtre qu'elle ouvre. */}

        {estCommandeFortemix(commande.articles.map((a) => a.reference), commande.fournisseurs) && (
          <ReleverFortemix commande={commande} />
        )}

        <div className="fieldset">
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <h3 style={{ marginBottom: 10 }}>Suivi transporteur</h3>
            {/* Le lien porté par la date d'expédition passait inaperçu : on le
                double ici d'un vrai bouton, à l'endroit où l'on cherche le suivi. */}
            {commande.tracking && (commande.liens.suivi ? (
              <a
                href={commande.liens.suivi} target="_blank" rel="noopener noreferrer"
                className="btn sm" style={{ whiteSpace: 'nowrap' }}
                title={`Suivre le colis chez ${commande.transporteur ?? 'le transporteur'}`}
              >
                Ouvrir le suivi ↗
              </a>
            ) : (
              <button
                type="button" className="btn sm" disabled
                title={commande.liens.suiviIndisponible ?? 'Suivi indisponible'}
              >
                Ouvrir le suivi ↗
              </button>
            ))}
          </div>
          {commande.tracking ? (
            <>
              <dl className="kv">
                <dt>Expédiée le</dt>
                <dd>
                  {/* Le lien vers la page du transporteur a suivi la donnée :
                      il vivait dans la liste, il vit là où elle s'affiche. */}
                  <LienExpedition commande={commande}>
                    {formatCourt(commande.dateExpedition)}
                  </LienExpedition>
                </dd>
                <dt>Transporteur</dt>
                <dd>{commande.transporteur ?? '—'}</dd>
                <dt>Dernier statut</dt>
                <dd style={{ textAlign: 'left' }}>
                  {commande.statutTransporteur || 'aucun point'}
                  {/* La commande ne se clôt qu'au dernier colis : on dit ce qu'elle attend. */}
                  {commande.premierColisLivre && (
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ink-3)' }}>
                      Commande pas encore livrée : {commande.colis.filter((c) => !c.livre).length} autre(s)
                      colis en route.
                    </span>
                  )}
                </dd>
                <dt>Mis à jour</dt>
                <dd>{formatCourt(commande.dateStatutTransporteur)}</dd>
                <dt>Livraison estimée</dt>
                <dd>
                  {commande.calcul.livraisonEstimee ? formatCourt(commande.calcul.livraisonEstimee.date) : '—'}
                </dd>
              </dl>
              {/* Relevé à la demande. Présent dès qu'il y a un numéro — y compris
                  sur un transporteur non outillé, où l'échec NOMME ce qui manque :
                  c'est ainsi qu'on vérifie chaque transporteur un par un. */}
              <div className="fieldset" style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button" className="btn sm" disabled={releve.isPending || suiviClos}
                      onClick={async () => {
                        setErreurReleve(null);
                        setVerdictReleve(null);
                        const avant = commande.statutTransporteur;
                        try {
                          const apres = await releve.mutateAsync({
                            id: commande.id,
                            ...(codePostalSaisi.trim() ? { codePostal: codePostalSaisi.trim() } : {}),
                          });
                          if (commande.livre || commande.premierColisLivre) {
                            const echecs = apres.colis.filter((c) => !c.livre && c.releveErreur).length;
                            setVerdictReleve(echecs
                              ? `Échec : ${echecs} colis non relevé(s) — détail ci-dessous.`
                              : 'Colis en route relevés : leur statut est ci-dessous.');
                          } else if (apres.releveErreur) {
                            setVerdictReleve(`Échec : ${apres.releveErreur}`);
                          } else if (apres.statutTransporteur === avant) {
                            setVerdictReleve(
                              avant
                                ? "Relevé abouti — le transporteur n'a rien de nouveau."
                                : "Relevé abouti — le transporteur ne connaît pas ce numéro.",
                            );
                          } else {
                            setVerdictReleve(`Relevé abouti : « ${apres.statutTransporteur} »`);
                            ajouter(`Suivi mis à jour : ${apres.statutTransporteur}`);
                          }
                        } catch (err) {
                          setErreurReleve(err instanceof ErreurApi ? err.message : 'Le relevé a échoué.');
                        }
                      }}
                    >
                      {releve.isPending ? <><span className="spin" /> Relevé…</> : 'Relever maintenant'}
                    </button>
                    <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      {suiviClos
                        ? 'Commande livrée : son suivi est clos — « Remettre en cours » pour la rouvrir.'
                        : commande.livre
                          ? 'Commande livrée, mais un colis est encore en route : lui seul est relevé.'
                          : commande.premierColisLivre
                            ? 'Premier colis livré : seuls les colis encore en route sont relevés.'
                          : <>Interroge <strong>{transporteurInterroge}</strong> directement.</>}
                    </span>
                  </div>

                  {/* DPD exige le code postal. Quand la commande ne le porte nulle
                      part — ni dans son champ, ni dans le bloc d'adresse Sellsy —, on
                      le demande ici : corriger l'adresse d'abord ferait perdre le fil,
                      et un code erroné ne fait rien remonter, le transporteur ne s'en
                      sert que pour confirmer. */}
                  {!codePostalConnu && !suiviClos && (
                    <div className="field" style={{ marginTop: 8 }}>
                      <label htmlFor="s-cp">Code postal du destinataire</label>
                      <input
                        id="s-cp" value={codePostalSaisi} inputMode="numeric"
                        onChange={(e) => setCodePostalSaisi(e.target.value)}
                        placeholder="exigé par DPD, absent de cette commande"
                      />
                      <p className="hint" style={{ margin: '4px 0 0' }}>
                        Sert à ce relevé seulement. Renseignez-le sur la commande pour qu&apos;il
                        serve aussi aux passages automatiques.
                      </p>
                    </div>
                  )}

                  {/* Le dernier relevé se lit à côté de la date d'événement : « relevé
                      il y a deux minutes, dernier mouvement il y a trois jours » n'est
                      pas la même information que les deux au même instant. */}
                  {commande.releveLe && (
                    <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '8px 0 0' }}>
                      Dernier relevé : {new Date(commande.releveLe).toLocaleString('fr-FR')}
                      {commande.releveErreur && <> — <span style={{ color: 'var(--brick)' }}>{commande.releveErreur}</span></>}
                    </p>
                  )}
                  {verdictReleve && (
                    <p
                      style={{ fontSize: 12, margin: '6px 0 0', color: verdictReleve.startsWith('Échec') ? 'var(--brick)' : 'var(--pine)' }}
                      role="status"
                    >
                      {verdictReleve}
                    </p>
                  )}
                </div>
              {erreurReleve && <p className="err" role="alert" style={{ marginTop: 8 }}>{erreurReleve}</p>}
              {commande.calcul.livraisonEstimee && (
                <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8 }}>
                  {commande.calcul.livraisonEstimee.source === 'transporteur'
                    ? 'Date communiquée par le transporteur.'
                    : "Estimation interne (expédition + délai de transit) : elle ne vaut pas confirmation, et ne clôture jamais la commande."}
                </p>
              )}
              <ColisSupplementaires commande={commande} modales={modales} />
            </>
          ) : (
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
              Le suivi démarrera dès que le tracking sera renseigné.
            </p>
          )}
        </div>


        <div className="fieldset">
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <button
              type="button" aria-expanded={articlesOuverts}
              onClick={() => setArticlesOuverts((o) => !o)}
              style={{
                display: 'flex', alignItems: 'baseline', gap: 8, flex: 1, minWidth: 0,
                padding: 0, background: 'none', border: 0, textAlign: 'left',
              }}
            >
              <span aria-hidden style={{ color: 'var(--ink-3)', fontSize: 10, width: 10, flexShrink: 0 }}>
                {articlesOuverts ? '▾' : '▸'}
              </span>
              <h3 style={{ marginBottom: articlesOuverts ? 10 : 0, flexShrink: 0 }}>Articles</h3>
              {/* Replié : un résumé d'une ligne, et un point rouge si une
                  anomalie se cache dessous. */}
              {!articlesOuverts && (
                <span
                  className="sub"
                  style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {commande.articles.length === 0
                    ? 'aucune ligne'
                    : commande.articles
                      .map((a) => `${a.quantite !== null ? `${a.quantite} × ` : ''}${a.nom ?? a.reference ?? '?'}`)
                      .join(' · ')}
                </span>
              )}
              {!articlesOuverts && (commande.referencesIndeterminees.length > 0
                || commande.articles.some((a) => !a.reference || !a.fournisseur)) && (
                <span title="Référence ou fournisseur manquant" style={{ color: 'var(--brick)', fontWeight: 700 }}>!</span>
              )}
            </button>
            {/* Une commande transmise a déjà été envoyée telle quelle : ses
                lignes ne se corrigent plus ici. */}
            {!commande.dateEnvoiFournisseur && (
              <button
                type="button" className="btn sm"
                onClick={() => modales.onArticles(commande)}
              >
                Modifier
              </button>
            )}
          </div>
          {articlesOuverts && (
            <>
              {commande.articles.length === 0 ? (
                <p style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
                  {commande.articlesRetires.length
                    ? 'Plus aucun article à commander : tout a été retiré par Shopify.'
                    : 'Aucune ligne produit sur cette commande.'}
                </p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {commande.articles.map((a, i) => (
                    <li
                      key={`${a.reference ?? 'sans-ref'}-${i}`}
                      style={{ padding: '7px 0', borderBottom: '1px solid var(--line)' }}
                    >
                      {/* La désignation d'abord : c'est elle qu'on lit. La référence
                          ne sert qu'à lever une ambiguïté. */}
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span style={{ fontWeight: 600 }}>
                          {a.nom ?? (
                            <span style={{ color: 'var(--ink-3)', fontWeight: 400, fontStyle: 'italic' }}>
                              désignation inconnue
                            </span>
                          )}
                        </span>
                        {a.quantite !== null && (
                          <span className="mono" style={{ color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>
                            ×{a.quantite}
                          </span>
                        )}
                      </div>
                      <div className="sub" style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                        <span className="mono">
                          {a.reference ?? <span style={{ color: 'var(--brick)' }}>référence absente</span>}
                        </span>
                        <span>
                          {a.fournisseur ?? <span style={{ color: 'var(--brick)' }}>fournisseur indéterminé</span>}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {/* Ce qui ne part PAS chez le fournisseur, et pourquoi. */}
              {commande.articlesRetires.length > 0 && (
                <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 10 }}>
                  Retiré par Shopify (remboursé ou annulé), non commandé :{' '}
                  {commande.articlesRetires
                    .map((r) => `${r.quantite} × ${r.reference ?? 'référence absente'}`).join(', ')}.
                </p>
              )}

              {commande.referencesIndeterminees.length > 0 && (
                <p className="err" style={{ marginTop: 10 }}>
                  Fournisseur indéterminé pour : {commande.referencesIndeterminees.join(', ')}.
                  Complétez le référentiel dans Réglages, ou corrigez la référence à la source.
                </p>
              )}
            </>
          )}
        </div>

        <div className="fieldset">
          <h3>Historique — du plus récent au plus ancien</h3>
          <ul className="timeline">
            {commande.histo.map((e, i) => (
              <li key={`${e.at}-${i}`}>
                <b>{dateHeure(e.at)}</b>
                {e.texte}
                <span className="qui"> — {e.auteur}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Clôturer, tout en bas et à part : ce n'est pas une action due parmi
            les autres, c'est déclarer une fin. Aucun clic de la liste ni du bloc
            d'action ne peut y mener — seulement ce bouton, et sa confirmation.

            Et son symétrique à la même place, une fois la commande close :
            défaire n'est pas non plus une action due, c'est la réparation d'un
            geste précédent, et on la cherche là où on l'a fait. */}
        {!commande.livre ? (
          <div className="fieldset">
            <h3>Clôturer</h3>
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 10 }}>
              À faire quand la livraison est constatée. La commande quitte le travail courant pour
              l&apos;archive, et son suivi cesse.
            </p>
            <button
              type="button" className="btn sm" disabled={enAttente}
              onClick={() => setConfirmationLivraison(true)}
            >
              Marquer livrée
            </button>
          </div>
        ) : (
          <div className="fieldset">
            <h3>Rouvrir</h3>
            <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 10 }}>
              La commande revient au travail courant, et son suivi reprend. Le statut du
              transporteur qu&apos;une clôture manuelle avait écrasé est restauré à
              l&apos;identique.
            </p>
            <button
              type="button" className="btn sm" disabled={enAttente}
              onClick={() => void remettreEnCours(commande)}
            >
              Remettre en cours
            </button>
          </div>
        )}
      </div>

      {fenetreEtape === 'reglement' && (
        <FenetreReglement commande={commande} onFermer={() => setFenetreEtape(null)} />
      )}

      {fenetreEtape === 'avis' && (
        <FenetreAvis commande={commande} onFermer={() => setFenetreEtape(null)} />
      )}

      {confirmationLivraison && (
        <ConfirmerLivraison
          commande={commande}
          enAttente={enAttente}
          onFermer={() => setConfirmationLivraison(false)}
          onConfirmer={async () => {
            await confirmerLivree(commande);
            setConfirmationLivraison(false);
          }}
        />
      )}
    </>
  );
}

/**
 * La cible de relance d'une action, ou `null` si l'action n'est pas une relance.
 *
 * Trois actions se relancent : « pas de date d'expédition », « commande
 * introuvable chez le fournisseur », et tout ce qui touche à la livraison. Pour
 * les deux premières, qui l'on relance dépend de la société (certaines SONT le
 * transport) ; pour la dernière, c'est le transport par définition.
 *
 * `probleme` manquait, et son absence privait de bouton la seule action qu'on
 * relance vraiment souvent hors de l'outil : une commande que le fournisseur ne
 * retrouve pas se règle au téléphone.
 */
function relanceDe(
  action: ActionCalculee,
  commande: CommandeDetaillee,
  reglages: DocumentReglages | undefined,
): CibleRelance | null {
  if (action.cle === 'livraison') return 'transporteur';
  if (action.cle === 'relance' || action.cle === 'probleme') {
    return cibleRelance(reglages ?? REGLAGES_PAR_DEFAUT, commande.fournisseurs);
  }
  /*
   * Réclamer un numéro de suivi est une relance comme une autre.
   *
   * Et elle se fait tout aussi bien au téléphone : c'est même le réflexe devant
   * un fournisseur qu'on a au bout du fil. Sans cette ligne, le geste n'était
   * consignable nulle part — le compteur de relances et la colonne « Litige »
   * ignoraient un appel qui avait bel et bien eu lieu.
   */
  if (action.cle === 'tracking') {
    return expedieLuiMeme(reglages ?? REGLAGES_PAR_DEFAUT, commande.fournisseurs)
      ? 'fournisseur'
      : null;
  }
  return null;
}

/**
 * Relève le portail fournisseur Fortemix pour cette commande.
 *
 * N'apparaît que sur les commandes qui en dépendent — une référence en `FCK` ou
 * `UTM` —, parce que le portail ne connaît pas les autres : un bouton qui
 * répondrait toujours « introuvable » n'apprendrait rien.
 *
 * Même code que le passage de 8 h 05 et 13 h 05 — un bouton qui ferait autre
 * chose que l'horaire annoncé serait un piège. Le portail fait foi : une date
 * ou un tracking qui y diffère est corrigé, pas seulement complété.
 *
 * **Une découverte part aussitôt chez le client**, comme avec « Actualiser »
 * ou le passage de 8 h 05 et 13 h 05 : la date par le mail d'expédition, le
 * tracking par le mail de suivi, sans relecture (décidé le 02/10/2026). Un
 * envoi qui échoue lève « Mail … non parti » dans « À faire ».
 */
function ReleverFortemix({ commande }: { commande: CommandeDetaillee }) {
  const { ajouter } = useToasts();
  const releve = useReleverFortemix();
  const [verdict, setVerdict] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const lancer = async () => {
    setVerdict(null);
    setErreur(null);
    try {
      const r = await releve.mutateAsync(commande.id);
      if (r.introuvable) {
        // Distinct d'une panne : le portail a répondu, il ne connaît pas cette
        // référence — sous aucune des trois formes essayées.
        setVerdict('Le portail Fortemix ne connaît pas cette commande.');
        return;
      }
      // « Corrigée » et « relevée » ne se lisent pas pareil : la première dit
      // qu'une date annoncée a bougé, ce qui se discute avec le client.
      const trouve = [
        r.dateTrouvee
          ? (commande.dateExpedition
            ? `date d'expédition corrigée (${formatCourt(commande.dateExpedition)} → ${formatCourt(r.commande.dateExpedition)})`
            : `date d'expédition relevée (${formatCourt(r.commande.dateExpedition)})`)
          : null,
        r.trackingTrouve
          ? (commande.tracking ? 'tracking corrigé' : 'tracking relevé')
          : null,
      ].filter(Boolean);

      if (!trouve.length) {
        setVerdict('Relevé abouti — Fortemix n\'a rien de neuf à ajouter.');
        return;
      }
      // Ce que la phrase promet doit être ce qui a eu lieu : on lit l'état du
      // mail après le relevé, pour ne pas dire le client prévenu à tort.
      // Un mail écarté plus tôt ne repart pas : il n'y a alors rien à en dire.
      const canaux = [r.dateTrouvee && 'expedition', r.trackingTrouve && 'tracking']
        .filter((c): c is 'expedition' | 'tracking' => Boolean(c))
        .filter((c) => r.commande.envois[c].etat !== 'ignore');
      const partis = canaux.every((c) => r.commande.envois[c].etat === 'envoye');
      setVerdict(
        `Relevé abouti : ${trouve.join(', ')}`
        + (!canaux.length ? '.'
          : partis ? ' — mail envoyé au client.'
          : ' — mail au client non parti, à reprendre dans « À faire ».'),
      );
      ajouter(`Fortemix : ${trouve.join(', ')} pour ${commande.ref}.`);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Le relevé Fortemix a échoué.');
    }
  };

  return (
    <div className="fieldset">
      <h3>Portail Fortemix</h3>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn sm" disabled={releve.isPending || commande.livre} onClick={() => void lancer()}>
          {releve.isPending ? <><span className="spin" /> Relevé…</> : 'Relever chez Fortemix'}
        </button>
        {/* L'URL vient du relevé qui a trouvé la commande, sous la forme de
            référence que Fortemix connaît : absente tant qu'il ne l'a pas trouvée. */}
        {commande.liens.portailFortemix && (
          <a
            href={commande.liens.portailFortemix} target="_blank" rel="noopener noreferrer"
            className="btn sm" style={{ whiteSpace: 'nowrap' }}
            title="Ouvrir la commande sur le portail Fortemix"
          >
            Ouvrir sur le portail ↗
          </a>
        )}
        <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
          {commande.livre
            ? 'Commande livrée : il n\'y a plus rien à relever.'
            : <>Lit la date de production et le lien de suivi publiés sur <strong>customer.fortemix.eu</strong>.</>}
        </span>
      </div>
      <p className="hint" style={{ margin: '8px 0 0' }}>
        Le portail fait foi : une date ou un tracking qui y diffère est corrigé, et le mail
        d&apos;expédition ou de suivi part aussitôt chez le client, sans relecture.
      </p>
      {verdict && (
        <p
          style={{
            fontSize: 12, margin: '6px 0 0',
            color: 'var(--pine)',
          }}
          role="status"
        >
          {verdict}
        </p>
      )}
      {erreur && <p className="err" role="alert" style={{ marginTop: 8 }}>{erreur}</p>}
    </div>
  );
}
